import { useEffect, useState } from 'react';
import { admin, palette, typography } from '../../styles/tokens';
import { useAuthStore } from '../../stores/authStore';
import WelcomeBlock from './components/WelcomeBlock';
import ProgressIndicator from './components/ProgressIndicator';
import ChecklistSection from './components/ChecklistSection';
import { useSetupGuideState, type ResolvedItem } from './hooks/useSetupGuideState';
import TaxItem from './items/TaxItem';
import HoursItem from './items/HoursItem';
import ServicesItem from './items/ServicesItem';
import BusinessProfileItem from './items/BusinessProfileItem';
import InvoiceBrandingItem from './items/InvoiceBrandingItem';
import ImportClientsItem from './importClients';
import StripeConnectItem from './stripeConnect';
import PlaceholderItem from './items/PlaceholderItem';
import SetupWizardItem from './items/SetupWizardItem';

export default function SetupGuidePage() {
  const state = useSetupGuideState();
  const businessId = useAuthStore(s => s.business?.id);
  const [selectedItemKey, setSelectedItemKey] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Setup Guide';
  }, []);

  const section1Items = state.items.filter(i => i.config.section === 1);
  const section2Items = state.items.filter(i => i.config.section === 2);
  const section3Items = state.items.filter(i => i.config.section === 3);

  const handleItemClick = (item: ResolvedItem) => {
    setSelectedItemKey(item.config.key);
  };

  const handleBack = () => setSelectedItemKey(null);
  const handleSaved = () => {
    state.refresh();
    setSelectedItemKey(null);
  };

  if (selectedItemKey && businessId) {
    const selected = state.items.find(i => i.config.key === selectedItemKey);
    if (selected) {
      if (selectedItemKey === 'setup_wizard') {
        return <SetupWizardItem businessId={businessId} onBack={handleBack} onSaved={handleSaved} />;
      }
      if (selectedItemKey === 'tax') {
        return <TaxItem businessId={businessId} onBack={handleBack} onSaved={handleSaved} />;
      }
      if (selectedItemKey === 'hours') {
        return <HoursItem businessId={businessId} onBack={handleBack} onSaved={handleSaved} />;
      }
      if (selectedItemKey === 'services') {
        return <ServicesItem businessId={businessId} onBack={handleBack} onSaved={handleSaved} />;
      }
      if (selectedItemKey === 'business_profile') {
        return (
          <BusinessProfileItem
            businessId={businessId}
            onBack={handleBack}
            onSaved={handleSaved}
          />
        );
      }
      if (selectedItemKey === 'invoice_branding') {
        return (
          <InvoiceBrandingItem
            businessId={businessId}
            onBack={handleBack}
            onSaved={handleSaved}
          />
        );
      }
      if (selectedItemKey === 'import_clients') {
        return (
          <ImportClientsItem
            onBack={handleBack}
            onSaved={handleSaved}
            skipItem={state.skipItem}
          />
        );
      }
      if (selectedItemKey === 'stripe_connect') {
        return (
          <StripeConnectItem
            onBack={handleBack}
            onSaved={handleSaved}
            skipItem={state.skipItem}
          />
        );
      }
      return (
        <PlaceholderItem
          itemKey={selectedItemKey}
          title={selected.config.title}
          onBack={handleBack}
        />
      );
    }
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '8px 4px 0' }}>
      <WelcomeBlock />

      {state.loading ? (
        <div
          style={{
            fontSize: typography.size.caption,
            color: admin.caption,
            marginBottom: 32,
          }}
        >
          Loading your setup progress…
        </div>
      ) : (
        <ProgressIndicator
          completedCount={state.completedCount}
          totalCount={state.totalCount}
          minutesRemaining={state.minutesRemaining}
        />
      )}

      <ChecklistSection
        title="Set up your business"
        items={section1Items}
        onItemClick={handleItemClick}
      />

      {section2Items.length > 0 && (
        <ChecklistSection
          title="Get ready for clients"
          items={section2Items}
          onItemClick={handleItemClick}
        />
      )}

      {section3Items.length > 0 && (
        <ChecklistSection
          title="Get paid and go live"
          items={section3Items}
          onItemClick={handleItemClick}
        />
      )}

      <div style={{ marginTop: 48, textAlign: 'center', paddingBottom: 24 }}>
        <p
          style={{
            margin: 0,
            color: palette.g500,
            fontSize: typography.size.caption,
          }}
        >
          Progress is saved automatically.
        </p>
      </div>
    </div>
  );
}
